/**
 * API Client
 *
 * Base fetch wrapper with:
 * - Auth headers (Bearer token)
 * - Tenant context headers
 * - Error handling
 * - Request/response typing
 * - Timeout handling
 */

import { useAuthStore } from "../store/authStore";
import type { ApiResponse, ApiError } from "../types";

/** API configuration */
interface ApiConfig {
  readonly baseUrl: string;
  readonly timeout: number;
}

/** Request options */
interface RequestOptions {
  readonly method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  readonly body?: unknown;
  /** Raw body string — sent as-is without JSON.stringify. Overrides `body`. */
  readonly rawBody?: string;
  readonly headers?: Record<string, string>;
  readonly timeout?: number;
}

/** Get API base URL from environment */
function getApiBaseUrl(): string {
  // Vite injects environment variables with VITE_ prefix
  const envUrl = import.meta.env.VITE_API_URL as string | undefined;
  return envUrl ?? "http://localhost:8787";
}

/** Default API configuration */
const defaultConfig: ApiConfig = {
  baseUrl: getApiBaseUrl(),
  timeout: 30000, // 30 seconds
};

/**
 * Make an authenticated API request.
 *
 * Automatically includes:
 * - Authorization header with Bearer token
 * - X-Tenant-ID header
 * - X-Environment header
 * - Content-Type: application/json
 */
export async function apiRequest<T>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<ApiResponse<T>> {
  const { method = "GET", body, rawBody, headers = {}, timeout = defaultConfig.timeout } = options;

  const authState = useAuthStore.getState();
  const url = `${defaultConfig.baseUrl}${endpoint}`;

  // Build headers
  const requestHeaders: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...headers,
  };

  // Add auth token if available
  if (authState.token) {
    requestHeaders["Authorization"] = `Bearer ${authState.token}`;
  }

  // Add tenant context
  if (authState.tenantId) {
    requestHeaders["X-Tenant-ID"] = authState.tenantId;
  }
  if (authState.environment) {
    requestHeaders["X-Environment"] = authState.environment;
  }

  // Create abort controller for timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const fetchOptions: RequestInit = {
      method,
      headers: requestHeaders,
      signal: controller.signal,
    };

    // Only add body for non-GET requests
    if (rawBody !== undefined) {
      fetchOptions.body = rawBody;
    } else if (body !== undefined) {
      fetchOptions.body = JSON.stringify(body);
    }

    const response = await fetch(url, fetchOptions);

    clearTimeout(timeoutId);

    // Parse response
    const responseText = await response.text();
    let data: unknown;

    try {
      data = JSON.parse(responseText);
    } catch {
      data = responseText;
    }

    // Handle non-2xx responses
    if (!response.ok) {
      const error = parseApiError(data, response.status);

      // Handle 401 - token expired or invalid
      if (response.status === 401) {
        authState.logout();
      }

      return { success: false, error };
    }

    // Unwrap standard API envelope { data: T, timestamp, requestId }
    const envelope = data as Record<string, unknown>;
    const payload =
      typeof data === "object" && data !== null && "data" in envelope ? envelope["data"] : data;

    return { success: true, data: payload as T };
  } catch (error) {
    clearTimeout(timeoutId);

    // Handle abort (timeout)
    if (error instanceof Error && error.name === "AbortError") {
      return {
        success: false,
        error: {
          code: "TIMEOUT",
          message: "Request timed out",
          timestamp: new Date().toISOString(),
        },
      };
    }

    // Handle network errors
    const message = error instanceof Error ? error.message : "Unknown error";
    return {
      success: false,
      error: {
        code: "NETWORK_ERROR",
        message,
        timestamp: new Date().toISOString(),
      },
    };
  }
}

/**
 * Parse API error response.
 */
function parseApiError(data: unknown, status: number): ApiError {
  const timestamp = new Date().toISOString();

  if (typeof data === "object" && data !== null) {
    const obj = data as Record<string, unknown>;

    // Check for standard error response format
    if (obj["error"] && typeof obj["error"] === "object") {
      const errorObj = obj["error"] as Record<string, unknown>;
      const details = errorObj["details"] as Record<string, unknown> | undefined;
      return {
        code: String(errorObj["code"] ?? `HTTP_${status}`),
        message: String(errorObj["message"] ?? "An error occurred"),
        ...(details !== undefined ? { details } : {}),
        timestamp: String(errorObj["timestamp"] ?? timestamp),
      };
    }

    // Legacy format
    if (obj["code"] && obj["message"]) {
      const details = obj["details"] as Record<string, unknown> | undefined;
      return {
        code: String(obj["code"]),
        message: String(obj["message"]),
        ...(details !== undefined ? { details } : {}),
        timestamp,
      };
    }
  }

  // Fallback
  return {
    code: `HTTP_${status}`,
    message: getDefaultErrorMessage(status),
    timestamp,
  };
}

/**
 * Get default error message for HTTP status code.
 */
function getDefaultErrorMessage(status: number): string {
  switch (status) {
    case 400:
      return "Bad request";
    case 401:
      return "Authentication required";
    case 403:
      return "Access denied";
    case 404:
      return "Resource not found";
    case 409:
      return "Conflict";
    case 422:
      return "Validation failed";
    case 429:
      return "Too many requests";
    case 500:
      return "Internal server error";
    case 502:
      return "Bad gateway";
    case 503:
      return "Service unavailable";
    default:
      return `HTTP error ${status}`;
  }
}
